import React, { useState, lazy, useEffect } from "react";
import { useJsApiLoader } from "@react-google-maps/api";

import Routes from "routes";

import { Locales } from "components/Locales/Locales";
import { NavigationScroll } from "layout/NavigationScroll";
import { RTLLayout } from "layout/RTLLayout";
import { Snackbar } from "components/Snackbar/Snackbar";
import { Loader } from "components/Loader/Loader";
import { LicenseInfo, muiXTelemetrySettings } from "@mui/x-license";
import ThemeCustomization from "themes";
import { JWTProvider as AuthProvider } from "hooks/contexts/JWTContext";
import { AppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { reactPlugin } from "./utils/ApplicationInsightsService";
import { UserRoleCheckDialog } from "./components/Dialogs/UserRoleCheckDialog";
import { suppressWarnings } from "./utils/globalOverrides";
import { serviceWorkerInit } from "./LocusServiceWorker";
import { SWReloadPopup } from "./components/Snackbar/SWReloadPopup";
import { useAuth } from "hooks/contexts/AuthContext";
import { storage } from "./utils/helpers/storage";

const IntroTourComponent = lazy(() => import("components/IntroTour/IntroTour"));

LicenseInfo.setLicenseKey(
  "732035635495487db2d55408b91eca7eTz0xMTIyMjAsRT0xNzc3NTkzNTk5MDAwLFM9cHJvLExNPXN1YnNjcmlwdGlvbixQVj1pbml0aWFsLEtWPTI=",
);

muiXTelemetrySettings.disableTelemetry(); // to disable telemetry collection and sending

const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const googleMapsLibraries = ["maps", "geocoding", "places"];

// Add this helper check for undefined URLs
function UndefinedUrlLogoutWatcher() {
  const { logout } = useAuth();

  useEffect(() => {
    if (storage.getKioskId()) return;
    if (window.location.href.includes("undefined")) {
      logout();
    }
  }, [logout]);
  return null;
}

const App = () => {
  suppressWarnings();
  /* https://github.com/JustFly1984/react-google-maps-api/tree/b768d0a528806bb5c40e66fc8564f94afeddff23/packages/react-google-maps-api */
  useJsApiLoader({
    id: "google-maps-provider",
    googleMapsApiKey: apiKey,
    libraries: googleMapsLibraries,
  });

  const [swReloadPopupOpen, setSwReloadPopupOpen] = useState(false);
  const notifyUser = (bool) => {
    setSwReloadPopupOpen(bool);
  };

  useEffect(() => {
    serviceWorkerInit(notifyUser);
    
    // Detect touch devices and add class for CSS fallback
    // This helps fix iOS "tap once to hover, tap again to click" issue
    if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
      document.body.classList.add('touch-device');
    }
  }, []);

  return (
    <AppInsightsContext.Provider value={reactPlugin}>
      <ThemeCustomization>
        <RTLLayout>
          <Locales>
            <NavigationScroll>
              <AuthProvider>
                <>
                  <UndefinedUrlLogoutWatcher />
                  <Routes />
                  <Snackbar />
                  <Loader />
                  <UserRoleCheckDialog />
                  <IntroTourComponent />
                  <SWReloadPopup show={swReloadPopupOpen}></SWReloadPopup>
                </>
              </AuthProvider>
            </NavigationScroll>
          </Locales>
        </RTLLayout>
      </ThemeCustomization>
    </AppInsightsContext.Provider>
  );
};

export default App;
